/**
 * PDFViewer Component
 * Renders PDF documents with all pages in a scrollable view.
 * Comments are overlaid on each page at their respective positions.
 */
import React, { useState, useCallback, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import PDFControls from './PDFControls';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

// Configure PDF.js worker - use CDN for production compatibility
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs`;

interface PDFViewerProps {
  fileUrl: string;
  onDocumentLoad?: (numPages: number) => void;
  className?: string;
  /** Render function for page overlays - receives page number and dimensions */
  renderPageOverlay?: (pageNumber: number, width: number, height: number) => React.ReactNode;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  fileUrl,
  onDocumentLoad,
  className = '',
  renderPageOverlay,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [scale, setScale] = useState<number>(1.0);
  const [, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pageDimensions, setPageDimensions] = useState<Map<number, { width: number; height: number }>>(
    new Map()
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      setLoading(false);
      setError(null);
      onDocumentLoad?.(numPages);
    },
    [onDocumentLoad]
  );

  const onDocumentLoadError = useCallback((error: Error) => {
    setError(`Failed to load PDF: ${error.message}`);
    setLoading(false);
  }, []);

  const onPageLoadSuccess = useCallback(
    (page: any) => {
      const pageNum = page.pageNumber;
      setPageDimensions((prev) => {
        const newMap = new Map(prev);
        newMap.set(pageNum, {
          width: page.width,
          height: page.height,
        });
        return newMap;
      });
    },
    []
  );

  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.25, 3.0));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  const resetZoom = useCallback(() => {
    setScale(1.0);
  }, []);

  const scrollToPage = useCallback((pageNumber: number) => {
    const pageElement = document.getElementById(`pdf-page-${pageNumber}`);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-8 text-red-600">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Controls */}
      <PDFControls
        pageNumber={1}
        numPages={numPages}
        scale={scale}
        onPrevPage={() => {}}
        onNextPage={() => {}}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onResetZoom={resetZoom}
        onGoToPage={scrollToPage}
        showPageNav={false}
      />

      {/* Scrollable PDF Document */}
      <div ref={containerRef} className="flex-1 overflow-auto bg-gray-200 p-4">
        <div className="flex flex-col items-center gap-4">
          <Document
            file={fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="flex items-center justify-center p-8">
                <span className="text-gray-500">Loading PDF...</span>
              </div>
            }
          >
            {Array.from({ length: numPages }, (_, index) => {
              const pageNumber = index + 1;
              const dims = pageDimensions.get(pageNumber);
              const scaledWidth = dims ? dims.width * scale : 612 * scale;
              const scaledHeight = dims ? dims.height * scale : 792 * scale;

              return (
                <div
                  key={pageNumber}
                  id={`pdf-page-${pageNumber}`}
                  className="relative mb-4 shadow-lg"
                  style={{ width: scaledWidth }}
                >
                  {/* Page number indicator */}
                  <div className="absolute -top-6 left-0 text-xs text-gray-500">
                    Page {pageNumber} of {numPages}
                  </div>

                  {/* PDF Page */}
                  <Page
                    pageNumber={pageNumber}
                    scale={scale}
                    onLoadSuccess={onPageLoadSuccess}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                  />

                  {/* Overlay for comments - positioned over the PDF page */}
                  {renderPageOverlay && (
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{ zIndex: 10 }}
                    >
                      <div className="relative w-full h-full">
                        {renderPageOverlay(pageNumber, scaledWidth, scaledHeight)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </Document>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
